import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { SEO } from "../components/SEO";
import { Reveal, Chapter, MaskLines } from "../components/Reveal";
import { SERVICES, ENGAGEMENT_NOTE } from "../data/content";

export default function Services() {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    }
  }, [hash]);

  return (
    <div data-testid="services-page">
      <SEO
        title="Services"
        description="Income tax, GST, audit and assurance, accounting, company and LLP services, business registrations, financial advisory, NRI taxation and professional certificates in Kurukshetra, Haryana."
        path="/services"
      />

      <section className="relative bg-navy text-white overflow-hidden" data-testid="services-hero">
        <div className="grain-overlay" />
        <div className="absolute -top-40 right-0 w-[480px] h-[480px] rounded-full bg-gold/10 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-36 pb-20 lg:pt-44 lg:pb-24">
          <p className="eyebrow mb-6">Professional Services</p>
          <h1 className="font-serif font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.1]" data-testid="services-heading">
            <MaskLines lines={["Comprehensive Services,"]} />
            <MaskLines lines={["One Trusted Practice."]} lineClassName="text-gold italic" baseDelay={0.3} />
          </h1>
          <Reveal delay={0.5} className="mt-7 max-w-2xl">
            <p className="text-slate-300 leading-relaxed">
              From routine compliance to considered financial advisory, each engagement is handled with professional
              rigour, confidentiality and timely delivery.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 sm:py-28 bg-background" data-testid="services-list">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {SERVICES.map((s, i) => (
            <div key={s.slug} id={s.slug} className="scroll-mt-28 py-12 first:pt-0 border-b border-slate-200 last:border-0" data-testid={`service-section-${s.slug}`}>
              <div className="grid lg:grid-cols-12 gap-10">
                <Reveal className="lg:col-span-5">
                  <div className="flex items-center gap-4 mb-5">
                    <span className="font-mono text-xs tracking-[0.3em] text-gold">{String(i + 1).padStart(2, "0")}</span>
                    <span className="gold-hairline w-14" />
                  </div>
                  <div className="flex items-start gap-5">
                    <span className="w-14 h-14 rounded-xl bg-navy flex items-center justify-center shrink-0">
                      <s.icon size={26} className="text-gold" strokeWidth={1.6} />
                    </span>
                    <div>
                      <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-navy tracking-tight">{s.title}</h2>
                      <p className="mt-3 text-slate-600 leading-relaxed text-sm sm:text-base">{s.short}</p>
                    </div>
                  </div>
                  <Link
                    to={`/contact?service=${encodeURIComponent(s.title)}`}
                    data-testid={`service-request-consultation-${s.slug}`}
                    className="mt-7 inline-flex items-center gap-2 rounded-full bg-navy px-6 py-3 text-sm font-semibold text-white hover:bg-gold hover:text-navy transition-colors"
                  >
                    Request Consultation <ArrowRight size={15} />
                  </Link>
                </Reveal>
                <Reveal delay={0.12} className="lg:col-span-7">
                  <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3.5 rounded-2xl bg-white border border-slate-200 p-7 sm:p-9">
                    {s.items.map((item) => (
                      <li key={item} className="flex items-start gap-3 text-sm text-slate-700">
                        <Check size={15} className="text-gold shrink-0 mt-0.5" strokeWidth={2.5} />
                        {item}
                      </li>
                    ))}
                  </ul>
                </Reveal>
              </div>
            </div>
          ))}

          <Reveal className="mt-14">
            <div className="rounded-xl bg-slate-100 border border-slate-200 p-6 sm:p-8" data-testid="services-engagement-note">
              <p className="text-xs font-mono uppercase tracking-[0.25em] text-gold mb-3">Please Note</p>
              <p className="text-xs sm:text-sm text-slate-500 leading-relaxed">{ENGAGEMENT_NOTE}</p>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
